import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LanguageToggle, Spinner } from "../components/ui";
import api, { formatApiError } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  // Seeded null, not with plausible-looking numbers: /stats can fail, and this is
  // the public page. "31 markets" sat here long after the real count reached 55.
  const [stats, setStats] = useState({ markets: null, listings: null, green: null });

  useEffect(() => {
    api.get("/stats").then(({ data }) => {
      setStats({
        markets: data.total_markets,
        listings: data.total_listings,
        green: data.green_zone_count,
      });
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await login(email, password);
      nav("/");
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col bg-bg bg-grid text-text-primary">
      <header className="h-16 shrink-0 border-b border-border bg-bg flex items-center justify-between gap-4 px-4 sm:px-8">
        <Logo />
        <div className="flex items-center gap-6 font-mono text-xs uppercase tracking-widest2 text-text-secondary">
          <span className="hidden sm:flex items-center gap-2">
            <span className="w-2 h-2 bg-signal-green inline-block" aria-hidden="true" />
            {t("auth.systemOnline")}
          </span>
          <span className="hidden md:inline">{t("auth.screenTimes")}</span>
          <LanguageToggle />
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-4 sm:p-10">
        <div className="w-full max-w-[560px] border border-border bg-surface reveal">
          <div className="flex items-center justify-between px-5 py-3 border-b border-border font-mono text-xs uppercase tracking-widest2 text-text-muted">
            <span>{t("auth.sessionAuth")}</span>
            <span>tty01</span>
          </div>
          <form
            onSubmit={submit}
            className="p-6 sm:p-8 flex flex-col gap-5"
            data-testid="login-form"
          >
            <div className="font-mono text-sm leading-relaxed text-text-secondary">
              <div><span className="text-signal-green">$</span> immo-watch login</div>
              <div>{t("auth.signInDesc")}</div>
            </div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl leading-none tracking-tight">
              {t("auth.signIn")}
            </h1>

            <PromptField
              id="login-email"
              label={t("auth.email")}
              type="email"
              autoComplete="email"
              value={email}
              onChange={setEmail}
            />
            <PromptField
              id="login-password"
              label={t("auth.password")}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={setPassword}
            />

            {err && (
              <div
                data-testid="login-error"
                className="border border-signal-red/40 bg-signal-red/10 text-signal-red text-xs px-3 py-2 font-mono"
              >
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="min-h-[56px] w-full bg-signal-green text-black font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 hover:bg-white transition-colors disabled:opacity-60"
            >
              {busy ? <Spinner /> : null}
              {busy ? t("auth.signingIn") : t("auth.enterDesk")}
              {!busy && <span aria-hidden="true">→</span>}
            </button>

            <div className="pt-5 border-t border-border flex items-center justify-between text-sm">
              <span className="text-text-secondary">{t("auth.noAccount")}</span>
              <Link
                to="/register"
                data-testid="login-register-link"
                className="font-mono text-xs uppercase tracking-widest2 hover:text-signal-green"
              >
                {t("auth.registerLink")}
              </Link>
            </div>
          </form>
        </div>
      </main>

      <footer className="shrink-0 border-t border-border bg-bg px-4 sm:px-8 py-3 flex flex-wrap items-center justify-between gap-2 font-mono text-xs uppercase tracking-widest2 text-text-muted">
        <span data-testid="login-stats">
          {[
            // Landing-only labels. shell.* feeds the AppShell nav and
            // auth.greenZone the Dashboard tile — reusing them here would
            // rename those too.
            [stats.markets, t("auth.statMarkets")],
            [stats.listings, t("auth.statListings")],
            [stats.green, t("auth.statRemaining")],
          ].map(([v, l]) => `${v ?? "—"} ${l}`).join(" · ")}
        </span>
        <span>{t("auth.version", { year: new Date().getFullYear() })}</span>
      </footer>
    </div>
  );
}

// A labelled input with a terminal-style ">" prompt. The prompt is decoration only;
// the visible <label> names the field.
function PromptField({ id, label, type, autoComplete, value, onChange }) {
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="font-mono text-xs uppercase tracking-widest2 text-text-secondary">
        {label}
      </label>
      <div className="flex items-center border border-zinc-700 bg-bg focus-within:border-signal-green">
        <span className="pl-4 font-mono text-signal-green" aria-hidden="true">&gt;</span>
        <input
          id={id}
          data-testid={id}
          type={type}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          className="flex-1 min-w-0 min-h-[52px] px-3 bg-transparent font-mono text-base text-white outline-none"
        />
      </div>
    </div>
  );
}

function Logo() {
  return (
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 bg-signal-green grid place-items-center">
        <div className="w-3 h-3 bg-black" />
      </div>
      <div className="font-heading font-black text-lg tracking-tight">immo-watch</div>
    </div>
  );
}
